import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSwipe } from "@/hooks/useSwipe";

// Full-width hero slider. All copy is sourced from riversol.com
// (homepage hero, meta description, product cards). Slide 1 leads with
// the broad brand promise; ingredient/product stories follow.
export const SLIDES = [
  {
    headline: ["Get the skin", "you want."],
    body:
      "Relief for sensitive skin, rosacea, dark spots and sun damage — developed by dermatologist Dr. Jason Rivers after 30+ years of clinical practice and research.",
    primary: { label: "Shop All Products", href: "https://www.riversol.com/collections/all" },
    secondary: { label: "Take the Skin Analyzer", href: "https://www.riversol.com/pages/skin-analyzer" },
    video:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/qc63rkn0_Home%20page%20video.mp4",
    focal: "50% 50%",
    tint: "from-black/55 via-black/15 to-transparent",
    alt: "Get the skin you want — Riversol",
  },
  {
    headline: ["Visibly plumps,", "cools + renews."],
    body:
      "Our new hydrogel mask harnesses PDRN, hyaluronic acid and hinokitiol to visibly firm and deeply hydrate your skin.",
    primary: {
      label: "Shop the mask",
      href: "https://www.riversol.com/products/hyaluronic-acid-pdrn-hydrogel-mask-combined",
    },
    secondary: { label: "Shop All", href: "https://www.riversol.com/collections/all" },
    image:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/2jk1dxzb_Mak-hidrogel.png",
    focal: "70% 50%",
    tint: "from-black/45 via-black/10 to-transparent",
    alt: "Hyaluronic Acid + PDRN Hydrogel Mask",
  },
];

const AUTOPLAY_MS = 7000;
const EASE = [0.16, 1, 0.3, 1];

const SlideProgress = ({ index, active, paused, dark, onClick }) => (
  <button
    type="button"
    aria-label={`Go to slide ${index + 1}`}
    aria-current={active ? "true" : undefined}
    data-testid={`home-hero-dot-${index}`}
    onClick={onClick}
    className="group relative py-3"
  >
    <span className={`block h-[2px] w-10 sm:w-14 overflow-hidden transition-colors ${dark ? "bg-neutral-900/25 group-hover:bg-neutral-900/40" : "bg-white/30 group-hover:bg-white/50"}`}>
      {active && (
        <motion.span
          key={`fill-${index}-${paused}`}
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: paused ? 0 : AUTOPLAY_MS / 1000, ease: "linear" }}
          className={`block h-full ${dark ? "bg-neutral-900" : "bg-white"}`}
          style={paused ? { width: "100%" } : undefined}
        />
      )}
    </span>
  </button>
);

export const HomeHero = ({ sticky = false, slides }) => {
  const DECK = slides && slides.length ? slides : SLIDES;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((v) => (v + 1) % DECK.length), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, DECK.length]);

  const go = useCallback((n) => setI(((n % DECK.length) + DECK.length) % DECK.length), [DECK.length]);

  const slide = DECK[i];
  const dark = !!slide.darkCopy;
  const swipe = useSwipe(() => go(i + 1), () => go(i - 1));

  return (
    <section
      className={`${sticky ? "sticky top-0 z-0" : "relative"} w-full bg-neutral-900 overflow-hidden`}
      style={{ height: "100svh", minHeight: 560, maxHeight: 860 }}
      data-testid="home-hero"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      {...swipe}
    >
      {/* IMAGE STAGE */}
      <AnimatePresence>
        <motion.div
          key={`img-${i}`}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.1, ease: EASE }, scale: { duration: 8, ease: "linear" } }}
        >
          {slide.video ? (
            <video
              src={slide.video}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ objectPosition: slide.focal }}
              data-testid={`home-hero-video-${i}`}
            />
          ) : (
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
              style={{ objectPosition: slide.focal }}
              data-testid={`home-hero-image-${i}`}
              draggable={false}
            />
          )}
          <div className={`absolute inset-0 bg-gradient-to-tr ${slide.tint} pointer-events-none`} />
          {!dark && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent pointer-events-none" />
          )}
        </motion.div>
      </AnimatePresence>

      {/* CONTENT */}
      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-12 xl:px-16 flex flex-col justify-end pb-24 sm:pb-20 lg:pb-20">
        <div className="max-w-[600px]">
          <h1
            className={`text-[34px] sm:text-[44px] lg:text-[54px] xl:text-[60px] leading-[1.06] tracking-[-0.02em] ${dark ? "text-neutral-900" : "text-white"}`}
            data-testid="home-hero-headline"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={`h-${i}`}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="block"
              >
                <span className="block">{slide.headline[0]}</span>
                <span className={`block italic ${dark ? "text-neutral-700" : "text-white/90"}`}>{slide.headline[1]}</span>
              </motion.span>
            </AnimatePresence>
          </h1>

          <AnimatePresence mode="wait">
            <motion.p
              key={`p-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.55, ease: EASE, delay: 0.06 }}
              className={`mt-4 sm:mt-5 text-[15px] sm:text-[16px] lg:text-[17px] leading-[1.6] max-w-[500px] ${dark ? "text-neutral-600" : "text-white/85"}`}
              data-testid="home-hero-subhead"
            >
              {slide.body}
            </motion.p>
          </AnimatePresence>

          <div className="mt-7 sm:mt-8 flex items-center gap-3 flex-wrap">
            <a
              href={slide.primary.href}
              data-testid="home-hero-primary-cta"
              className={`group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[12px] sm:text-[13px] tracking-[0.14em] uppercase font-medium transition-colors ${dark ? "bg-neutral-900 text-white hover:bg-riversol-blue" : "bg-white text-neutral-900 hover:bg-riversol-blue hover:text-white"}`}
            >
              {slide.primary.label}
              <ArrowRight size={15} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={slide.secondary.href}
              data-testid="home-hero-secondary-cta"
              className={`inline-flex items-center rounded-full border px-6 py-3.5 text-[12px] sm:text-[13px] tracking-[0.14em] uppercase font-medium transition-colors ${dark ? "border-neutral-900/30 text-neutral-900 hover:bg-neutral-900/5 hover:border-neutral-900/60" : "border-white/45 text-white hover:bg-white/10 hover:border-white/80"}`}
            >
              {slide.secondary.label}
            </a>
          </div>

          {/* TRUST PROOF */}
          <div
            className={`mt-7 sm:mt-8 flex items-center gap-2 text-[12px] sm:text-[13px] ${dark ? "text-neutral-700" : "text-white/90"}`}
            data-testid="home-hero-trust"
          >
            <span className={`tracking-[0.18em] text-[12px] ${dark ? "text-neutral-900" : "text-white"}`}>★★★★★</span>
            <span>115,000+ happy customers</span>
          </div>
        </div>
      </div>

      {/* SLIDER INDICATORS — bottom right */}
      <div className="absolute inset-x-0 bottom-0 z-10 pointer-events-none">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-12 xl:px-16 pb-5 sm:pb-6 flex justify-end">
          <div className="pointer-events-auto flex items-center gap-2 sm:gap-3" data-testid="home-hero-indicators">
            <span className={`font-mono text-[11px] tracking-[0.22em] uppercase mr-1 sm:mr-2 tabular-nums ${dark ? "text-neutral-900/70" : "text-white/70"}`}>
              {String(i + 1).padStart(2, "0")}
              <span className={dark ? "text-neutral-900/40" : "text-white/40"}> / {String(DECK.length).padStart(2, "0")}</span>
            </span>
            {DECK.map((_, idx) => (
              <SlideProgress key={idx} index={idx} active={idx === i} paused={paused} dark={dark} onClick={() => go(idx)} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
